import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AlertCircle, CreditCard, ExternalLink } from 'lucide-react';
import moment from 'moment';

import { useAppDispatch, useAppSelector } from '../../hooks.ts';
import { subscriptionReducer } from '../../store/reducers/subscription.reducer.ts';
import { getIsOpeningPaymentPage, getSubscription } from '../../store/selectors/subscription.selector.ts';
import { getUser } from '../../store/selectors/user.selector.ts';
import { SubscriptionTier } from '../../types/subscription.ts';

const { loadSubscription, openBillingPortal } = subscriptionReducer.actions;

const TIER_LABEL_KEYS: Record<SubscriptionTier, string> = {
  TRIAL: 'settings.plans.trial',
  FREE: 'settings.plans.free',
  PROFESSIONAL: 'settings.plans.professional',
  ULTIMATE: 'settings.plans.ultimate',
};

/**
 * Billing overview. Payment method, plan changes, cancellation and invoices live on the payment
 * provider's portal, so this section shows where the user stands and links there.
 */
export const BillingSettings: React.FC<{ onViewPlans: () => void }> = ({ onViewPlans }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const subscription = useAppSelector(getSubscription);
  const user = useAppSelector(getUser);
  const isOpeningPaymentPage = useAppSelector(getIsOpeningPaymentPage);

  useEffect(() => {
    dispatch(loadSubscription());
  }, [dispatch]);

  const tier: SubscriptionTier = subscription?.tier || 'FREE';
  const isProviderManaged = !!subscription?.providerManaged;
  const isPastDue = subscription?.paymentStatus === 'PAST_DUE';

  const periodText = (): string => {
    if (!isProviderManaged || !subscription?.currentPeriodEnd) return t('settings.billing.notPaidPlan');
    const date = moment(subscription.currentPeriodEnd).format('MMMM DD, YYYY');
    return subscription.cancelAtPeriodEnd
      ? t('settings.plans.endsOn', { date })
      : t('settings.plans.renewsOn', { date });
  };

  return (
    <div className="flex-1 overflow-y-auto bg-black p-4 md:p-8" style={{ minHeight: '0' }}>
      <div className="max-w-3xl space-y-6">
        <section className="rounded-lg border border-gray-800 p-6 md:p-8" data-testid="billing-current-plan">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-400 mb-4">
            <CreditCard size={14} />
            {t('settings.billing.currentPlan')}
          </div>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-4xl font-light text-white mb-2">{t(TIER_LABEL_KEYS[tier])}</div>
              <div className="text-sm text-gray-400">{periodText()}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-light text-white">{user?.balance ?? 0}</div>
              <div className="text-sm text-gray-400">{t('settings.billing.creditsAvailable')}</div>
            </div>
          </div>

          {isPastDue && (
            <div className="mt-6 flex items-start gap-2 rounded-lg border border-orange-500/40 bg-orange-500/10 p-4 text-sm text-orange-300">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              {t('settings.plans.paymentFailed')}
            </div>
          )}

          <button
            onClick={onViewPlans}
            className="mt-6 px-6 py-3 rounded-full font-medium text-xs border border-gray-600 text-white hover:bg-greyP5 transition-colors duration-200"
          >
            {t('settings.billing.viewPlans')}
          </button>
        </section>

        <section className="rounded-lg border border-gray-800 p-6 md:p-8">
          <h2 className="text-xl font-medium text-white mb-2">{t('settings.billing.manageTitle')}</h2>
          <p className="text-sm text-gray-400 mb-6">{t('settings.billing.manageDescription')}</p>
          <button
            onClick={() => dispatch(openBillingPortal())}
            disabled={isOpeningPaymentPage}
            className="flex items-center gap-2 px-6 py-3 rounded-full font-medium text-xs bg-green01 text-black hover:bg-green02 transition-colors duration-200 disabled:cursor-not-allowed disabled:bg-zinc-600 disabled:text-zinc-400"
          >
            {t('settings.billing.manage')}
            <ExternalLink size={14} />
          </button>
        </section>
      </div>
    </div>
  );
};
